//3rd系
import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useDailyEvent, DailyProvider } from '@daily-co/daily-react';
import DailyIframe from '@daily-co/daily-js';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import isElectron from './Global';
import { useTranslation } from 'react-i18next';
import { dailyStateAtom, isAuthAtom, userTypeAtom } from './State';
import HairCheck from './components/HairCheck/HairCheck';
import Call from './components/Call/Call2';
import Tray from './components/Tray/Tray';
import Loading from './components/Loading/Loading';
import Waiting from './components/Loading/Waiting';
import Header from './components/Header/Header';
import Media from './components/Media/Media';
import CenterLogo from './components/Header/CenterLogo';
import AuthCheck from './AuthCheck';
import ErrorBoundary from './components/Util/ErrorBoundary';
import DataModel from './components/Util/DataModel';
import Live2dWrap from './components/Live2d/Live2dWrap';
import LoadingFace from './components/Live2d/LoadingFace';
import Select from './components/Select/Select';
import Resize from './components/Live2d/Resize';
import ParticipantJoined from './components/Call/ParticipantJoined';
import Feedback from './components/Call/Feedback';
import AddAccess from './components/Util/AddAccess';
import Live from './components/Live/Live';
import FuncLeaveRoom from './components/Meeting/FuncLeaveRoom';
import BGM from './components/Call/BGM';
import TraySpeech from './components/Tray/TraySpeech';

//ミーティング全体
export default function Meeting({ live = false, recording = false }) {
  //Recoil系
  const [dailyState, setDailyState] = useRecoilState(dailyStateAtom); //dailyの接続ステータス
  const [userTypeState, setUserTypeState] = useRecoilState(userTypeAtom); //dailyの接続ステータス
  const [authState, setAuthState] = useRecoilState(isAuthAtom);
  const { t } = useTranslation();

  //State系
  const [roomUrl, setRoomUrl] = useState(''); //ルームURL
  const [callObject, setCallObject] = useState(null); //コールオブジェクト
  const [apiError, setApiError] = useState(false); //APIエラー
  const [loadingState, setLoadingState] = useState(false); //ローディングアイコン表示用
  const [waitingState, setWaitingState] = useState(false); //待機中の場合
  const [preAuthCompleteState, setPreAuthCompleteState] = useState(false);
  const [accessState, setAccessState] = useState(false); //アクセス許可状況
  const [liveDateState, setLiveDataState] = useState(null); //アクセス許可状況
  const [isLeaveState, setIsLeaveState] = useState(false);

  //Ref系
  const dailyURLRef = useRef(null);
  const meetingTokenRef = useRef(null);
  const screenShotRef = useRef(null); //スクリーンショットを撮るときの範囲
  const userType = useRef('speaker');
  const initialLoad = useRef(false);

  //ルートURL系
  const navigate = useNavigate();
  const urlParam = useParams();
  var roomID = urlParam.id; //URL取得
  var tokenID = urlParam.token; //URL取得
  //const [searchParams, setSearchParams] = useSearchParams();
  //const tokenValue = searchParams.get('token') ?? null;
  // roomID = meetingIDMobile == null ? roomID : meetingIDMobile;
  // tokenID = meetingTokenMobile == null ? tokenID : meetingTokenMobile;

  if (live && initialLoad.current == false) {
    //URLを書き換え
    //window.history.pushState({}, null, '/' + roomID);

    //トークンIDがある場合
    if (tokenID != null) {
      //スピーカー
      userType.current = 'speaker';
    } else {
      //ビューワー
      userType.current = 'viewer';
    }

    //初回ロード
    initialLoad.current = true;
  }

  //ブラウザバックボタンを押された時
  useEffect(() => {
    //ライブモードの場合

    //アクセス
    AddAccess('page-view', 'haircheck');

    if (isMobile) {
      AddAccess('page-view', 'accessFromMobile');
    }

    //エラー処理1
    window.addEventListener('error', (event) => {
      //console.log(Error(event.message ?? event));
      const { message, filename, lineno, colno, error } = event;
      console.log('エラー:', message, filename, lineno, colno, error.stack);

      //データ保存
      //  DataModel.sendError(message);
    });

    //エラー処理2
    window.onunhandledrejection = (event) => {
      console.warn(`rejectionエラー: ${event.reason}`);
      console.log(event);
    };

    //エラー処理3
    window.addEventListener('rejectionhandled', function (event) {
      console.warn(`rejectionエラー: ${event.reason}`);
    });

    //ヒストリーを追加
    window.history.pushState(null, null, window.location.href);

    //バックボタンが押された時;
    window.onpopstate = (e) => {
      if (dailyState == 'STATE_JOINED') {
        //ヒストリーを上書き
        window.history.pushState(null, null, window.location.href);

        //確認画面
        var result = window.confirm(t('haircheck.Are you sure you want to end call?'));
        if (result) {
          if (callObject != null) {
            callObject.destroy();
          }
          //ルームを離れる
          setIsLeaveState(true);
        } else {
          return false;
        }
      } else {
        //ルームを離れる
        setIsLeaveState(true);
        return true;
      }
    };
  }, []);

  //URLのルームが存在するかチェック
  useEffect(() => {
    //アプリにリダイレクト
    redirectToApp();

    if (recording) {
      prepareRecording();
    }
    //フェイス選択画面の場合
    else if (roomID == 'face') {
      setDailyState('STATE_SELECT_FACE');
    } else {
      //ルームIDの存在チェック (遅延処理を入れないと,context.uid がサーバー側で取得できない)
      setTimeout(() => {
        checkRoomAPI();
      }, 10);
    }

    //リセット
    return () => resetState();
  }, []);

  //エレクトロンに移動
  function redirectToApp() {
    if (isElectron == false) {
      var tokenID2 = tokenID ?? 'none'; //URL取得
      let islive = live ? 'yes' : 'no';

      // let externalURL =
      //   'faceless://?type=meeting&id=' + roomID + '&tokenID=' + tokenID2 + '&isLive=' + islive;

      // try {
      //   window.location.assign(externalURL);
      // } catch (e) {
      //   console.log(e);
      // }
    }
  }

  //Stateのリセット(ブラウザバッグ用)
  function resetState() {
    setDailyState('STATE_IDLE');
  }

  //録画モードの場合
  async function prepareRecording() {
    const roomInfo = await DataModel.prepareRecording();

    if (roomInfo != null) {
      //Daily用のURL
      dailyURLRef.current = 'https://faceless.daily.co/' + roomInfo.dailyRoomURL;
      //ミーティングトークン
      meetingTokenRef.current = roomInfo.meetingToken;
      //ユーザータイプ
      userType.current = 'owner';
      setUserTypeState('owner');
    }

    //オーナーとスピーカーの場合
    var dataParam = {
      audioSource: {
        echoCancellation: true, //defaultはtrue
        sampleRate: { ideal: 32000 }, //defaultは48000
        //echoCancellationType: 'system', //Macの場合は最強らしい defaultは system
      },
      //audioSource:true,
      videoSource: false,
      dailyConfig: {
        experimentalChromeVideoMuteLightOff: true, // So a Chrome participant doesn't see a green light when video is off
      },
    };

    //コールオブジェクトの作成
    const newCallObject = DailyIframe.createCallObject(dataParam);

    let url = dailyURLRef.current;
    setRoomUrl(url);
    setCallObject(newCallObject);
    //setDailyState('STATE_PREJOIN');
    setDailyState('STATE_JOINED');
    //プレAuth処理
    var joinObj = {
      url: url,
      userData: { userType: userType.current },
      token: meetingTokenRef.current,
    };

    //ローディング画像表示
    //setLoadingState(true);

    setAccessState(true);

    //ジョインコール
    await newCallObject.join(joinObj);

    setPreAuthCompleteState(true);
  }

  // 1.ルーム存在チェック処理
  async function checkRoomAPI() {
    try {
      //roomIDとmeetingTokenの2つを取得

      const roomInfo = await DataModel.checkRoom(roomID, live, tokenID);

      //alert(roomInfo.dailyRoomURL);

      //ルームが存在しない場合
      if (roomInfo.dailyRoomURL == null) {
        alert('URL is not valid. No room exist');
        //強制的にトップページヘ
        if (authState) {
          navigate('/app');
        } else {
          window.location.href = 'https://faceless.ooo';
        }
      }
      //ルームが存在する場合
      else {
        //Daily用のURL
        dailyURLRef.current = roomInfo.dailyRoomURL;
        //ミーティングトークン
        meetingTokenRef.current = roomInfo.meetingToken;
        //ユーザータイプ
        let isOwner = roomInfo.isOwner ?? false;
        let isSpeaker = roomInfo.isSpeaker ?? false;

        //ライブモードの場合
        if (live) {
          if (isOwner && meetingTokenRef.current != null) {
            userType.current = 'owner';
            setUserTypeState('owner');
          } else if (isSpeaker) {
            userType.current = 'speaker';
            setUserTypeState('speaker');
          } else {
            userType.current = 'viewer';
            setUserTypeState('viewer');
          }
        }
        //通常ミーティングの場合
        else {
          if (meetingTokenRef.current != null && isOwner) {
            userType.current = 'owner';
            setUserTypeState('owner');
          } else {
            userType.current = 'speaker';
            setUserTypeState('speaker');
          }
        }

        //共通
        if (roomInfo.liveData != null) {
          setLiveDataState(roomInfo.liveData);
        }

        //プレジョイン開始
        startPreJoin();
      }
    } catch (error) {
      console.log('ネットワークエラー');
      setRoomUrl(null);
      setDailyState('STATE_ERROR');
      setApiError(true);

      //アクセス
      AddAccess('error', 'haircheck', 'no room for this url');
    }
  }

  // 2.Pre参加スタート
  async function startPreJoin() {
    //オーナーとスピーカーの場合
    var dataParam = {
      audioSource: {
        echoCancellation: true, //defaultはtrue
        sampleRate: { ideal: 32000 }, //defaultは48000
        //echoCancellationType: 'system', //Macの場合は最強らしい defaultは system
      },
      //audioSource:true,
      videoSource: false,
      dailyConfig: {
        experimentalChromeVideoMuteLightOff: true, // So a Chrome participant doesn't see a green light when video is off
      },
    };

    //ビューワーの場合
    if (userType.current == 'viewer') {
      dataParam = {
        audioSource: false,
        videoSource: false,
        dailyConfig: {
          experimentalChromeVideoMuteLightOff: true, // So a Chrome participant doesn't see a green light when video is off
        },
      };
    }

    //コールオブジェクトの作成
    const newCallObject = DailyIframe.createCallObject(dataParam);

    let url = dailyURLRef.current;
    setRoomUrl(url);
    setCallObject(newCallObject);
    setDailyState('STATE_PREJOIN');

    //プレAuth処理
    var joinObj = { url: url };

    //ミーティングトークンがある場合
    if (meetingTokenRef.current != null) {
      joinObj.token = meetingTokenRef.current;
    }

    //プレAuth開始
    const preJoinStatus = await newCallObject.preAuth(joinObj);

    //プレAuth完了
    setPreAuthCompleteState(true);
    console.log('プレジョイン完了');

    //アクセス
    AddAccess('pre-joined', 'haircheck');
  }

  // 3.本番参加スタート
  const joinCall = useCallback(async () => {
    //Join処理
    var joinObj = { url: roomUrl };
    if (meetingTokenRef.current != null) {
      joinObj.token = meetingTokenRef.current;
    }

    //ユーザーデータ
    joinObj.userData = {
      userType: userType.current,
    };

    //現状のアクセス権限取得
    const { access } = callObject.accessState();

    if (access?.level == 'lobby') {
      //待機中ローディングの表示
      setWaitingState(true);
    } else {
      //ローディング画像表示
      setLoadingState(true);
    }

    //ジョインコール
    await callObject.join(joinObj);

    //まだ待機室（ロビー）にいる場合（下記はjoin後ででないと発動しない）
    if (access?.level == 'lobby') {
      //アクセス要求には名前が必須
      var name = 'facelsss';
      if (localStorage.getItem('displayName') != null) {
        name = localStorage.getItem('displayName');
      }

      //アクセスをリクエストする
      const { granted } = await callObject.requestAccess({
        name: name,
        access: {
          level: 'full',
        },
      });

      if (granted) {
        // Note: we don't have to do any thing here as the call state will mutate
        console.log('👋 Access granted');
        setAccessState(true);
      } else {
        console.log('❌ Access denied');
        //setDenied(true);
        setAccessState(false);
      }
    }
    //一旦下記に変更
    else {
      setAccessState(true);
    }
    //オーナの場合または既にアクセス許可されている場合
    // else if (access?.level == 'full') {
    //   setAccessState(true);
    // }
  }, [callObject, roomUrl]);

  // 4. ルームを離れる
  const startLeavingCall = useCallback(() => {
    if (!callObject) return;

    if (dailyState === 'STATE_ERROR') {
      callObject.destroy().then(() => {
        setRoomUrl(null);
        setCallObject(null);
        setDailyState('STATE_IDLE');

        if (isElectron) {
          window.myapi.closeWindow();
        }
      });
    } else {
      if (isElectron) {
        // window.myapi.leaveMeeting();
        callObject.leave();
        window.myapi.leaveMeeting();
      } else {
        callObject.leave();
      }
    }

    //ルームを離れる
    setIsLeaveState(true);
  }, [callObject, dailyState]);

  // トップ画面へ
  const exitCall = async () => {
    if (isElectron) {
      window.myapi.leaveMeeting();
    } else {
      //ログインしている場合
      if (authState) {
        //window.location.href = 'https://faceless.ooo/app';
        navigate('/app', { state: { comeFromExit: true } });
      }
      //ログインしていない場合
      else {
        window.location.href = 'https://faceless.ooo';
      }
    }
  };

  //******** コールオブジェクトの状況変化を監視  ************ //

  useEffect(() => {
    if (!callObject) return;

    const events = ['joined-meeting', 'joining-meeting', 'left-meeting', 'error', 'camera-error'];

    function handleNewMeetingState(event) {
      //現状のミーティング状況の取得
      switch (callObject.meetingState()) {
        case 'joining-meeting':
          console.log('ジョイン中');
          setDailyState('STATE_JOINING');
          break;
        case 'joined-meeting':
          console.log('ジョイン完了');
          setDailyState('STATE_JOINED');
          break;
        case 'left-meeting':
          callObject.destroy().then(() => {
            setRoomUrl(null);
            setCallObject(null);
            setIsLeaveState(true);
            setDailyState('STATE_FEEDBACK');
          });
          break;
        case 'participant-left':
          console.log(event);
          console.log(callObject);
          break;
        case 'error':
          setDailyState('STATE_ERROR');
          navigate('/');
          break;

        default:
          break;
      }
    }

    //ミーティング状況の監視（初回読み込み処理）
    handleNewMeetingState();

    //各種トリガーをリッスン
    for (const event of events) {
      callObject.on(event, handleNewMeetingState);
    }

    //リッスンを停止
    return function cleanup() {
      for (const event of events) {
        callObject.off(event, handleNewMeetingState);
      }
    };
  }, [callObject]);

  //アクセス状況
  useEffect(() => {
    if (accessState) {
      //ローディング中と待機中を非表示に
      setLoadingState(false);
      setWaitingState(false);
    }
  }, [accessState]);

  //********  レンダー部分  ************ //

  var showType = 'preJoin';

  //録画
  if (recording) {
    showType = 'recording';
  }
  //フィードバック
  else if (dailyState == 'STATE_FEEDBACK') {
    showType = 'feedback';
  }
  //APIエラーの場合
  else if (apiError) {
    console.log('エラー');
  }
  //顔選択画面
  else if (dailyState == 'STATE_FACE_SELECT') {
    showType = 'selectFace';
  }
  //メイン画面表示
  else if (['STATE_JOINED'].includes(dailyState) && accessState == true) {
    showType = 'mainJoin';
  }
  //ライブでビューワーの場合
  else if (live && userType.current == 'viewer') {
    showType = 'liveViewer';
  }
  //プレJoin画面表示
  else if (
    ['STATE_PREJOIN', 'STATE_JOINING', 'STATE_ERROR', 'STATE_JOINED'].includes(dailyState) &&
    accessState == false
  ) {
    showType = 'preJoin';
  } else if (isElectron) {
  } else if (isMobile && userType.current != 'viewer') {
    // return (
    //   <div id="notSupportMobile">
    //     <a href="https://faceless.ooo">
    //       <img src="https://d2wp3sepl8g4xv.cloudfront.net/hp/images/logo.png" alt="Photo" />
    //     </a>
    //     <h1>Currectly mobile browser is not supported </h1>
    //     <span>Please use your browser from the PC / Mac.</span>
    //     <a href="https://faceless.ooo" id="notSupportedButton">
    //       Go home
    //     </a>
    //   </div>
    // );
    //showType = 'preJoin';
  }
  //アプリ/ブラウザ選択画面
  else {
    // return <Select joinBrowser={joinBrowser} dailyState={dailyState} />;
  }

  return (
    <div className={isMobile ? 'isMobile' : 'appElectron'}>
      <div id="headerBlack"></div>
      <ErrorBoundary>
        <DailyProvider
          callObject={callObject}
          // recoilRootProps={{
          //   // stores Daily React's state in RecoilRoot above
          //   override: false,
          // }}
        >
          {/*　認証 */}
          <AuthCheck />

          {/*　部屋を離れる時用 */}
          <FuncLeaveRoom isLeave={isLeaveState} />

          {/*　待機ローディング */}
          <Waiting waitingState={waitingState} leaveCall={startLeavingCall} />

          {/*　ローディング */}
          <Loading loadingState={loadingState} />

          {/* 顔認識ローディング中 */}
          {!isMobile && userType.current != 'viewer' && <LoadingFace />}

          {/* メディア系(顔認識と音声) */}
          {userType.current != 'viewer' && <Media />}

          <TraySpeech />

          {/*　ヘッダー */}
          <Header
            url={roomUrl}
            leaveCall={startLeavingCall}
            isHairCheck={showType == 'preJoin' && true}
            isLive={live}
            isLiveViewer={live && userType.current == 'viewer' ? true : false}
            isRecord={recording}
          />

          {/* リサイズ中表示 */}
          <Resize />

          {/* BGM */}
          <BGM />

          {/*　顔選択の表示 */}
          {showType == 'selectFace' && (
            <>
              <HairCheck selectFace={true} />
            </>
          )}

          {/*　録画専用の表示 */}
          {showType == 'recording' && (
            <>
              {/*　メインコール画面 */}
              <Call
                roomID={roomID}
                userType={userType.current}
                isRecord={true}
                preAuthCompleteState={preAuthCompleteState}
              />
            </>
          )}

          {/*　プレJoin画面の表示 */}
          {showType == 'preJoin' && (
            <>
              {/*　Prejoin画面 */}
              <HairCheck
                joinCall={joinCall}
                preAuthCompleteState={preAuthCompleteState}
                liveData={liveDateState}
              />
            </>
          )}

          {/*　joing後の画面の表示 */}
          {showType == 'mainJoin' && (
            <>
              {/*　メインコール画面 */}
              <Call roomID={roomID} userType={userType.current} />
              {/* 他の参加者の参加と離れるのを検知　*/}
              <ParticipantJoined leaveCall={startLeavingCall} isLive={live} />
            </>
          )}

          {/*　Live時のビューワー画面 */}
          {showType == 'liveViewer' && (
            <>
              {/*　LiveViewer用画面 */}
              <Live
                joinCall={joinCall}
                preAuthCompleteState={preAuthCompleteState}
                liveData={liveDateState}
                roomID={roomID}
              />
            </>
          )}

          {/*　フィードバックの表示 */}
          {showType == 'feedback' && <Feedback exitCall={exitCall} />}

          {/*　Live2d表示 */}
          <div className="call">
            <Live2dWrap
              isHairCheck={showType == 'preJoin' ? true : false}
              isLiveViewer={live && userType.current == 'viewer' ? true : false}
              isLive={live}
              isRecord={recording}
            />
          </div>

          {/*　フッター */}
          <Tray
            leaveCall={startLeavingCall}
            screenShotRef={screenShotRef}
            isRecording={recording}
            isHairCheck={showType != 'mainJoin' ? true : false}
            isLiveViewer={live && userType.current == 'viewer' ? true : false}
            isLive={live}
          />
        </DailyProvider>
      </ErrorBoundary>
    </div>
  );
}
